import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { ERoleAdminString } from '../../../../shared/data/role.enum';
import { SetupService } from '../../services/setup.service';
import { SessionService } from '../../../../shared/services/session.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { IModuleForm, IModuleGraphConfig } from '../../models/setup.interface';
import { ICompany } from '../../../companies/models/company.interface';
import { CompanyService } from '../../../companies/services/company.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { EMessageType } from '../../../../shared/data/response-default.interface';
import { ModuleService } from '../../services/module.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ThemeOptions } from '../../models/select-options-config-setup';
import { NAVIGATION_CONFIG } from '../setting-module/enum/enum-type';
import {
  DEFAULT_REGISTRATION,
  createRegistrationGroup,
  getAgeGenderModuleNames
} from '../setup-identification/setup-identification';
import { ModalConfirmacaoService } from '../../../../shared/services/modal-confirmacao.service';
import {
  getMissingDependencies,
  getModulesDependingOn,
  getRequiredModuleIndex,
  getSelectedModuleRoutes,
  getUnselectedModuleRoutes,
  isRequiredByFlow,
  MODULE_DISPLAY_NAMES,
  REQUIRED_FLOW_MODULES
} from '../../models/module-dependencies.const';

@Component({
  selector: 'app-add-new-setup',
  templateUrl: './add-new-setup.component.html',
  styleUrls: ['./add-new-setup.component.scss']
})
export class AddNewSetupComponent implements OnInit {
  ERoleAdminString = ERoleAdminString;
  formOpen = false;
  readonly anonymousControl = new UntypedFormControl(false);
  readonly registrationGroup = createRegistrationGroup();
  formSetup = new UntypedFormGroup({
    name: new UntypedFormControl('', [Validators.required]),
    companyId: new UntypedFormControl(null, [Validators.required]),
    setupConfig: new UntypedFormGroup({
      setupTheme: new UntypedFormControl(null, [Validators.required]),
      checkValidPayment: new UntypedFormControl(false),
      hasTelemedicine: new UntypedFormControl(false),
      allowAnonymousUsers: this.anonymousControl,
      registration: this.registrationGroup,
      accessibilityVoice: new UntypedFormControl(false),
      home: new UntypedFormControl(null)
    })
  });

  companies: ICompany[] = [];

  optionsTheme = ThemeOptions;
  selectedModuleId: string | null = null;
  showConfigModal = false;

  private allModules: IModuleForm[] = [];
  modules: IModuleForm[] = [];
  availableModules: IModuleForm[] = [];
  homeModuleOptions: IModuleForm[] = [];
  ageGenderModuleNames: string[] = [];

  @Output() reloadSetups = new EventEmitter();

  constructor(
    private readonly setupService: SetupService,
    private readonly moduleService: ModuleService,
    private readonly companyService: CompanyService,
    private readonly sessionService: SessionService,
    private readonly toastService: ToastService,
    private readonly modalConfirmacao: ModalConfirmacaoService
  ) {}

  ngOnInit(): void {
    // Só Admin escolhe a empresa; os demais usam a da sessão e não têm company.list.
    if (this.sessionService.getSession()?.role === ERoleAdminString.Admin) {
      this.companyService.listarEmpresasAtivas().subscribe((response) => {
        this.companies = response.data;
      });
    }

    const companyId = this.sessionService.getSession()?.companyId;
    if (companyId) {
      this.formSetup.get('companyId')?.setValue(companyId);
    }

    this.moduleService.getAll().subscribe((response) => {
      this.allModules = response.data.map((module) => ({
        ...module,
        hasConfigModule: this.hasConfig(module.configSchema),
        checked: false
      }));
      this.availableModules = [...this.allModules];
    });
  }

  public saveSetup(): void {
    if (this.formSetup.invalid) {
      this.toastService.toast('Preencha todos os campos obrigatórios antes de salvar.', EMessageType.Error);
      this.formSetup.markAllAsTouched();
      return;
    }

    if (this.modules.length === 0) {
      this.toastService.toast('Setup deve ter ao menos um módulo.', EMessageType.Error);
      return;
    }

    if (!this.validateModuleDependencies()) {
      return;
    }

    const modulesPayload = this.modules.map((module, index) => ({
      moduleId: module.id,
      order: index + 1,
      configModule: JSON.stringify(module.configModule)
    }));

    const setupPayload = {
      ...this.formSetup.getRawValue(),
      modules: modulesPayload
    };

    this.setupService.addSetup(setupPayload).subscribe((resposta) => {
      if (resposta.isSucceed) {
        this.toastService.toast('Setup cadastrado com sucesso!');
        this.close();
        this.reloadSetups.emit();
      } else {
        this.toastService.toast(resposta.messages || 'Erro ao cadastrar setup.');
      }
    });
  }

  openModuleSettingModal(moduleId: string): void {
    this.selectedModuleId = moduleId;
    this.showConfigModal = true;
  }

  handleModuleConfig({ moduleId, config }: { moduleId: string; config: any }): void {
    const moduleToUpdate = this.modules.find((m) => m.id === moduleId);
    if (moduleToUpdate) {
      const previousConfig = moduleToUpdate.configModule;
      const newConfig = { ...previousConfig, ...config };
      moduleToUpdate.configModule = newConfig;
      this.removeUnselectedModulesFromFlow(moduleToUpdate, previousConfig, newConfig);
      this.addSelectedModulesToFlow(moduleToUpdate, config);
    } else {
      console.error('ERRO: Módulo para atualizar não foi encontrado no array "modules".');
    }
    this.showConfigModal = false;
  }

  private addSelectedModulesToFlow(module: IModuleForm, config: Record<string, unknown>): void {
    getSelectedModuleRoutes(module.configSchema, config).forEach((route) => {
      const moduleToAdd = this.availableModules.find((m) => m.route === route);
      if (moduleToAdd) {
        this.addModule(moduleToAdd);
      }
    });
  }

  private removeUnselectedModulesFromFlow(
    module: IModuleForm,
    previousConfig: Record<string, unknown> | null | undefined,
    newConfig: Record<string, unknown>
  ): void {
    getUnselectedModuleRoutes(module.configSchema, previousConfig, newConfig).forEach((route) => {
      const moduleToRemove = this.modules.find((m) => m.route === route);
      if (moduleToRemove) {
        this.removeModule(moduleToRemove);
      }
    });
  }

  hasConfig(obj: any): boolean {
    if (!obj) return false;
    const navKeys = Object.values(NAVIGATION_CONFIG);
    return Object.keys(obj).some((key) => !navKeys.includes(key as any));
  }

  drop(event: CdkDragDrop<IModuleForm[]>): void {
    moveItemInArray(this.modules, event.previousIndex, event.currentIndex);
    this.updateHomeModuleOptions();
    this.updateDisplayConnections();
  }

  addModule(moduleToAdd: IModuleForm): void {
    this.insertModule(moduleToAdd, this.modules.length);
    this.addRequiredModules();
    this.updateHomeModuleOptions();
  }

  private insertModule(moduleToAdd: IModuleForm, index: number): void {
    this.availableModules = this.availableModules.filter((m) => m.id !== moduleToAdd.id);
    this.modules.splice(index, 0, {
      ...moduleToAdd,
      configModule: this.getDefaultConfig()
    });
  }

  // Insere no fluxo o Auth e a Triagem que algum módulo do fluxo requer e que ainda não estão nele.
  private addRequiredModules(): void {
    REQUIRED_FLOW_MODULES.forEach(({ route, flag }) => {
      const requiredModule = this.availableModules.find((m) => m.route === route);
      if (requiredModule && isRequiredByFlow(this.modules, flag)) {
        this.insertModule(
          requiredModule,
          getRequiredModuleIndex(
            route,
            this.modules.map((m) => m.route)
          )
        );
      }
    });
  }

  removeModule(moduleToRemove: IModuleForm): void {
    const dependents = getModulesDependingOn(moduleToRemove.route).filter((depRoute) =>
      this.modules.some((m) => m.route === depRoute)
    );

    if (dependents.length > 0) {
      const dependentNames = dependents.map((r) => MODULE_DISPLAY_NAMES[r] || r).join(', ');
      this.modalConfirmacao
        .abrirModalConfirmacao({
          mensagem: `O módulo "${moduleToRemove.name}" é obrigatório para: ${dependentNames}. Deseja remover mesmo assim?`,
          textoBotaoConfirma: 'Remover',
          textoBotaoCancela: 'Cancelar'
        })
        .subscribe((confirmed) => {
          if (confirmed) {
            this.executeRemoveModule(moduleToRemove);
          }
        });
      return;
    }

    this.executeRemoveModule(moduleToRemove);
  }

  // A posição é buscada na hora: a remoção pode ter esperado o modal de dependência, e nesse meio
  // tempo outros módulos podem ter saído do fluxo.
  private executeRemoveModule(moduleToRemove: IModuleForm): void {
    const index = this.modules.findIndex((m) => m.id === moduleToRemove.id);
    if (index === -1) return;
    this.modules.splice(index, 1);
    this.availableModules.push(moduleToRemove);
    this.availableModules.sort((a, b) => a.name.localeCompare(b.name));

    this.modules.forEach((m) => {
      if (m.configModule?.connections) {
        m.configModule.connections = m.configModule.connections.filter((id: string) => id !== moduleToRemove.id);
      }
    });
    this.updateHomeModuleOptions();
    this.updateDisplayConnections();
  }

  getPossibleConnections(currentModule: IModuleForm, currentIndex: number): IModuleForm[] {
    const connections = currentModule.configModule?.connections || [];
    const connectedIds = new Set(connections);
    const existingConnections = this.modules.filter((m) => connectedIds.has(m.id));
    let potentialNewConnections: IModuleForm[] = [];
    if (currentModule.multipleConnections === false && connections.length >= 1) {
      potentialNewConnections = [];
    } else {
      potentialNewConnections = this.modules.filter((optionModule, optionIndex) => {
        return optionIndex > currentIndex || optionModule.multipleConnections === true;
      });
    }
    const finalOptions = [...existingConnections, ...potentialNewConnections];
    const finalOptionsMap = new Map<string, IModuleForm>();
    finalOptions.forEach((module) => {
      if (module.id !== currentModule.id) {
        finalOptionsMap.set(module.id, module);
      }
    });
    return Array.from(finalOptionsMap.values());
  }

  toggleConnection(parentModule: IModuleForm, childModuleId: string): void {
    const parentConfig = parentModule.configModule as IModuleGraphConfig;
    const isAlreadyConnected = parentConfig.connections.includes(childModuleId);
    const childModule = this.modules.find((m) => m.id === childModuleId);
    if (!childModule) return;

    if (isAlreadyConnected) {
      parentConfig.connections = parentConfig.connections.filter((id) => id !== childModuleId);
      if (childModule.configModule!.previousModule === parentModule.id) {
        childModule.configModule!.previousModule = null;
      }
    } else {
      parentConfig.connections.push(childModuleId);
      const parentIndex = this.modules.findIndex((m) => m.id === parentModule.id);
      const childIndex = this.modules.findIndex((m) => m.id === childModule.id);
      if (parentIndex < childIndex) {
        childModule.configModule!.previousModule = parentModule.id;
      }
    }
    this.updateDisplayConnections();
  }

  private updateDisplayConnections(): void {
    const allModulesMap = new Map(this.allModules.map((m) => [m.id, m]));
    this.modules.forEach((parentModule) => {
      const config = parentModule.configModule as IModuleGraphConfig;
      if (config.connections?.length > 0) {
        parentModule.displayConnections = config.connections
          .map((id) => allModulesMap.get(id))
          .filter((m): m is IModuleForm => !!m);
      } else {
        parentModule.displayConnections = [];
      }
    });
  }

  private validateModuleDependencies(): boolean {
    const presentRoutes = this.modules.map((m) => m.route);

    for (const module of this.modules) {
      const missing = getMissingDependencies(module.route, presentRoutes);
      if (missing.length > 0) {
        const missingNames = missing.map((r) => MODULE_DISPLAY_NAMES[r] || r).join(', ');
        this.toastService.toast(
          `O módulo "${module.name}" requer: ${missingNames}. Adicione-os antes de salvar.`,
          EMessageType.Warning
        );
        return false;
      }
    }
    return true;
  }

  hasUnmetDependencies(module: IModuleForm): boolean {
    const presentRoutes = this.modules.map((m) => m.route);
    return getMissingDependencies(module.route, presentRoutes).length > 0;
  }

  getMissingDependencyNames(module: IModuleForm): string[] {
    const presentRoutes = this.modules.map((m) => m.route);
    return getMissingDependencies(module.route, presentRoutes).map((r) => MODULE_DISPLAY_NAMES[r] || r);
  }

  private updateHomeModuleOptions(): void {
    this.homeModuleOptions = this.modules.filter((m) => m.multipleConnections === true);
    this.ageGenderModuleNames = getAgeGenderModuleNames(this.modules);
    const homeControl = this.formSetup.get('setupConfig.home');
    if (homeControl && homeControl.value) {
      const selectedHomeStillExists = this.homeModuleOptions.some((m) => m.id === homeControl.value);
      if (!selectedHomeStillExists) {
        homeControl.setValue(null);
      }
    }
  }

  isConnected(parentModule: IModuleForm, childModuleId: string): boolean {
    return parentModule.configModule?.connections?.includes(childModuleId) ?? false;
  }

  private getDefaultConfig(): IModuleGraphConfig {
    return { canReturn: false, connections: [], previousModule: null };
  }

  public getModuleConfigForModal(): any {
    if (!this.selectedModuleId) {
      return null;
    }
    const module = this.modules.find((m) => m.id === this.selectedModuleId);
    return module ? module.configModule : null;
  }

  close(): void {
    this.formOpen = false;
    this.formSetup.reset();
    // reset() deixaria a navegação anônima e o cadastro em null no próximo setup.
    this.anonymousControl.reset(false);
    this.registrationGroup.reset({ ...DEFAULT_REGISTRATION });
    this.modules = [];
    this.availableModules = [...this.allModules];
  }
}
